'use client';

import { useMemo, useState } from 'react';
import {
  combinedProbabilities,
  parseFiniteNumber,
  parseProbability,
  singleEventProbability,
  type ProbabilityResult,
} from '@/lib/browser-mathcalc-tools';

export default function BrowserProbabilityCalculator() {
  const [favorable, setFavorable] = useState('1');
  const [total, setTotal] = useState('6');
  const [pA, setPA] = useState('0.5');
  const [pB, setPB] = useState('0.5');
  const [pAnd, setPAnd] = useState('');

  const single = useMemo(
    () => singleEventProbability(parseFiniteNumber(favorable), parseFiniteNumber(total)),
    [favorable, total],
  );

  const combined = useMemo(
    () => combinedProbabilities(parseProbability(pA), parseProbability(pB), parseProbability(pAnd)),
    [pA, pB, pAnd],
  );

  const singleError = !Array.isArray(single) ? single.error : null;
  const singleRows: ProbabilityResult[] = Array.isArray(single) ? single : [];

  return (
    <div className="tool-live-shell">
      <div className="tool-live-header">
        <span className="tool-live-badge">Probability</span>
        <h2 className="tool-live-title">Probability Calculator</h2>
        <p className="tool-live-subtitle">
          Work out a single-event probability from favorable and total outcomes, then combine two
          events. Everything updates live in your browser.
        </p>
      </div>

      <div className="tool-live-grid">
        <section className="tool-live-uploader">
          <div className="tool-live-controls">
            <label className="tool-live-field">
              <span>Favorable outcomes</span>
              <input
                inputMode="numeric"
                onChange={(event) => setFavorable(event.target.value)}
                placeholder="1"
                type="text"
                value={favorable}
              />
            </label>
            <label className="tool-live-field">
              <span>Total outcomes</span>
              <input
                inputMode="numeric"
                onChange={(event) => setTotal(event.target.value)}
                placeholder="6"
                type="text"
                value={total}
              />
            </label>
            <label className="tool-live-field">
              <span>P(A)</span>
              <input
                inputMode="decimal"
                onChange={(event) => setPA(event.target.value)}
                placeholder="0.5 or 50%"
                type="text"
                value={pA}
              />
            </label>
            <label className="tool-live-field">
              <span>P(B)</span>
              <input
                inputMode="decimal"
                onChange={(event) => setPB(event.target.value)}
                placeholder="0.5 or 50%"
                type="text"
                value={pB}
              />
            </label>
            <label className="tool-live-field tool-live-field-full">
              <span>P(A and B) — optional, leave blank for independent events</span>
              <input
                inputMode="decimal"
                onChange={(event) => setPAnd(event.target.value)}
                placeholder="Leave blank to assume independence"
                type="text"
                value={pAnd}
              />
            </label>
          </div>
          <p className="tool-live-panel-note">
            Enter probabilities as a decimal (0.25) or a percentage (25%). Combined values assume
            the two events are independent unless you supply P(A and B).
          </p>
        </section>

        <section className="tool-live-preview">
          <h3 className="tool-live-title" style={{ fontSize: '1.05rem' }}>
            Single event
          </h3>
          {singleError ? (
            <p className="status-message" style={{ color: '#dc2626' }}>
              {singleError}
            </p>
          ) : singleRows.length === 0 ? (
            <p className="tool-live-panel-note">
              Enter favorable and total outcomes to see results.
            </p>
          ) : (
            <div className="tool-html-meta-grid">
              {singleRows.map((row) => (
                <div className="tool-live-field" key={row.label}>
                  <span>{row.label}</span>
                  <input readOnly type="text" value={row.value} />
                  {row.note ? <small style={{ opacity: 0.7 }}>{row.note}</small> : null}
                </div>
              ))}
            </div>
          )}

          <h3 className="tool-live-title" style={{ fontSize: '1.05rem', marginTop: '1rem' }}>
            Two events
          </h3>
          {combined.length === 0 ? (
            <p className="tool-live-panel-note">Enter P(A) and P(B) to combine two events.</p>
          ) : (
            <div className="tool-html-meta-grid">
              {combined.map((row) => (
                <div className="tool-live-field" key={row.label}>
                  <span>{row.label}</span>
                  <input readOnly type="text" value={row.value} />
                  {row.note ? <small style={{ opacity: 0.7 }}>{row.note}</small> : null}
                </div>
              ))}
            </div>
          )}
          <p className="tool-live-panel-note">Calculated locally in your browser.</p>
        </section>
      </div>
    </div>
  );
}
